/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import Page from '../../components/PageWrapper';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, Users, TrendingUp, ChevronRight, Lock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/images/Brolly Logo.svg';
import Techstars from '../../assets/images/techstars.svg'
import EastChop from '../../assets/images/eastChop.svg'
import MushaVentures from '../../assets/images/mushaVentures.svg'
import AfricaFintech from '../../assets/images/africaFintech.svg'
import Bolt from '../../assets/images/bolt.svg'
import Shell from '../../assets/images/shell.svg'
import useModal from '../../hooks/modalHook';
interface IAuthWrapperProps {
  children: JSX.Element;
  pageTitle?: string;
  callToActionMsg?: string;
}

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const valueProps = [
  "Create a customized store in 5mins",
  "Reduce time spent on policy creation by 70%",
  "AI sales team: create policies even when your team is absent",
  "AI claims management to detect fraud etc."
]

function AuthWrapper({ children, pageTitle, callToActionMsg }: IAuthWrapperProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get('tab') || 'sign-in';
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(tab === 'sign-in');
  const [activeFeature, setActiveFeature] = useState(0);
  const { ModalComponent, showModal, closeModal, updateModalStates } = useModal()


  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setShowLogin(tab === 'sign-in');
  }, [tab]);

  // Update URL when tab changes
  const handleTabChange = (isLogin: boolean) => {
    setShowLogin(isLogin);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', isLogin ? 'sign-in' : 'register');
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  // partner list
  const partnerList = [
    Techstars,
    EastChop,
    MushaVentures,
    AfricaFintech,
    Bolt,
    Shell
  ]

  // Features list
  const features: Feature[] = [
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: 'Advanced Analytics',
      description: 'Gain valuable insights with our comprehensive analytics dashboard.'
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Client Management',
      description: 'Streamline customer data and policy management in one place.'
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: 'Business Intelligence',
      description: 'Make data-driven decisions with powerful business insights.'
    },
    {
      icon: <Lock className="h-6 w-6" />,
      title: 'Enterprise Security',
      description: 'Rest easy with our bank-level security and compliance features.'
    }
  ];

  return (
    <Page title={pageTitle}>
      <div className="min-h-screen flex bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Left side - Form */}
        <div className="w-full lg:w-1/2 flex flex-col p-8 md:p-16 relative z-10">
          <div>
            <div>
              <img src={logo} alt="Brolly Logo" className="w-20 h-20" />
            </div>

            <div className="mt-10">
              <motion.h1
                key={showLogin ? 'login' : 'register'}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-5xl font-bold text-gray-900 tracking-tight leading-tight">
                {showLogin
                  ? 'Welcome\nback'
                  : 'Join\nBrolly'}
              </motion.h1>
              <motion.p
                key={showLogin ? 'login-desc' : 'register-desc'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-4 text-lg text-gray-600">
                {showLogin
                  ? 'Sign in to access your insurance management dashboard'
                  : 'Create your account to get started with Brolly'}
              </motion.p>

              {/* Toggle between Login and Register */}
              <div className="mt-8 flex space-x-6">
                <motion.button
                  onClick={() => handleTabChange(true)}
                  className={`relative px-6 py-3 text-lg font-medium rounded-full ${showLogin ? 'bg-gray-900 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  whileHover={{ scale: showLogin ? 1 : 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Sign in
                </motion.button>
                <motion.button
                  onClick={() => handleTabChange(false)}
                  className={`relative px-6 py-3 text-lg font-medium rounded-full ${!showLogin ? 'bg-gray-900 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  whileHover={{ scale: !showLogin ? 1 : 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Register
                </motion.button>
              </div>


              <div className="mt-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={showLogin ? 'login' : 'register'}
                    initial={{ opacity: 0, x: showLogin ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: showLogin ? 20 : -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl shadow-sm p-8 border border-gray-200"
                  >
                    {children}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-12 text-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} Brolly Insurance. All rights reserved.</p>
            <div className="mt-3 flex justify-center space-x-6">
              <Link to="#" className="hover:text-gray-900 transition-colors">Privacy</Link>
              <Link to="#" className="hover:text-gray-900 transition-colors">Terms</Link>
              <Link to="#" className="hover:text-gray-900 transition-colors">Support</Link>
            </div>
          </motion.div>
        </div>

        {/* Right side - Features */}
        <div className="hidden lg:block relative w-0 flex-1 pb-10">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 min-h-full overflow-y-auto">

            {/* Animated background elements */}
            <div className="absolute inset-0 opacity-15">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: `${Math.random() * 12 + 4}px`,
                    height: `${Math.random() * 12 + 4}px`,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    opacity: Math.random() * 0.4 + 0.1,
                    animation: `float ${Math.random() * 15 + 10}s linear infinite`,
                    animationDelay: `${Math.random() * 5}s`
                  }}
                />
              ))}
            </div>

            <div className="relative flex flex-col h-full p-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="w-full min-h-full"
              >
                <h2 className="text-2xl font-bold text-white tracking-tight">
                  Brolly Intelligence Center
                </h2>

                <h3 className="mt-10 text-lg font-semibold text-gray-100 tracking-tight">
                  Comprehensive Insurance Management
                </h3>

                <p className="mt-6 text-gray-300 leading-relaxed">
                  Streamline your insurance operations with our comprehensive artificial intelligent platform.
                </p>

                <div className="mt-12">
                  {features.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: activeFeature === index ? 1 : 0.5,
                        y: 0,
                        scale: activeFeature === index ? 1 : 0.98
                      }}
                      transition={{ duration: 0.5 }}
                      className={`mb-4 p-4 rounded-xl ${activeFeature === index ? 'bg-white/10 backdrop-blur-sm' : ''}`}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 p-2 rounded-lg bg-white/20">
                          {feature.icon}
                        </div>
                        <div className="ml-4">
                          <h3 className="text-xl font-semibold text-white flex items-center">
                            {feature.title}
                            {activeFeature === index && (
                              <ChevronRight className="h-5 w-5 ml-2 text-gray-400" />
                            )}
                          </h3>
                          <p className="mt-2 text-sm text-gray-300">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-10">
                  <div className="flex items-center">
                    <div className="flex -space-x-3 overflow-hidden">
                      {partnerList.map((partner, index) => (
                        <img key={index} src={partner} alt={`Partner ${index + 1}`} className="inline-block h-10 w-10 object-contain rounded-full bg-gray-200 border-2 border-gray-800 shadow-md" />
                      ))}
                    </div>
                    <div className="ml-5">
                      <p className="text-sm font-medium text-gray-100">Trusted by industry leaders</p>
                    </div>
                  </div>

                  <div className="flex items-center text-gray-400 text-sm mt-10">
                    <div className="w-2 h-2 rounded-full bg-green-400 mr-2"></div>
                    <span>Secure enterprise-grade platform</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        <ModalComponent />
      </div>
    </Page >
  );
}

export { AuthWrapper };


{/* children */ }
{/* {props.children} */ }

{/* Welcome */ }
{/* <div className='w-full font-poppins'>
          <h3 className='font-bold text-[36px] leading-[45px] md:text-[60px] md:leading-[70px] text-black font-poppins'>Welcome to <br /> Brolly</h3>
          <p className='text-black py-4'>{callToActionMsg}</p>
          <h3 className='text-black font-bold pb-2 text-[18px] mt-2'>AI solutions to grow your sales</h3>

          {valueProps.map((valueProp: string, index: number) => (
            <div key={index} className="flex items-center space-y-2">
              <CheckMark className='w-[10px] h-[10px] mr-2 md:mr-4' />
              <span className='text-[12px] md:text-[13px text-black h-full mb-2'>{valueProp}</span>
            </div>
          ))}

          <div>
            <SignupImage className='absolute right-0 bottom-[50px]' />
          </div>
        </div> */}